import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StringParam, useQueryParam } from 'use-query-params';
import { ResourceType } from '@setvi/shared/enums';
import { Resource } from '@setvi/shared/interfaces';

import type { CoverForm, PrintOrderItem } from '../types';
import { COVER_FIELD_KEYS } from '../constants';
import { usePrintOrderStore } from '../store';
import {
  DynamicPdfType,
  getActiveShoppingCartQuery,
  getShoppingCartByIdQuery,
  ShoppingCartDetail,
  ShoppingCartDynamicPdf
} from '../api/shopping-cart';
import { usePrintOrderCart } from './use-print-order-cart';
import { usePrintOrderEnabled } from './use-print-order-enabled';

// The logo comes back as bare base64 or as a data url; <img> needs the latter.
// Browsers sniff the real image type, so the declared one only has to be valid.
const toDataUrl = (base64: string) =>
  base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;

// The details a saved cover was generated with, so editing the line starts
// from what the rep entered rather than the profile. Every field counts as
// decided: an empty one prints blank instead of being prefilled again. The
// uploaded logo is restored too, so a regenerate keeps it; without one the
// company logo applies, as when the cover was first made.
const toCoverFields = (cover: ShoppingCartDynamicPdf): CoverForm => ({
  values: {
    ContactName: cover.CoverContactName ?? '',
    ContactTitle: cover.CoverContactTitle ?? '',
    PhoneNumber: cover.CoverPhoneNumber ?? '',
    EmailAddress: cover.CoverEmailAddress ?? '',
    Website: cover.CoverWebsite ?? ''
  },
  touched: [...COVER_FIELD_KEYS],
  logoBase64: cover.LogoBase64 ? toDataUrl(cover.LogoBase64) : undefined
});

// Build a cart item from a saved cart detail. Restores the QR / cover
// customization from the line's `DynamicPdfs` so the preview + edit dialog show
// the generated PDFs after a refresh (instead of losing them).
const toPrintOrderItem = (detail: ShoppingCartDetail): PrintOrderItem => {
  const resourceId = Number(detail.ResourceId);

  const dynamicPdfs = detail.DynamicPdfs ?? [];
  const qr = dynamicPdfs.find(pdf => pdf.Type === DynamicPdfType.Qr);
  const cover = dynamicPdfs.find(pdf => pdf.Type === DynamicPdfType.Cover);
  const locationPdf = dynamicPdfs.find(
    pdf => pdf.Type === DynamicPdfType.Location
  );

  return {
    resource: {
      ID: resourceId,
      ResourceID: String(resourceId),
      Name: detail.ProductName ?? detail.Filename,
      FileName: detail.Filename,
      // The line's own PDF (used for the preview / as its fallback).
      DownloadURL: detail.DownloadURL ?? undefined,
      DownloadURLWithSas: detail.DownloadURLWithSas ?? undefined,
      ThumbURLWithSas:
        detail.ThumbnailUrl ?? detail.ResourceThumbnailURLWithSas ?? undefined,
      ResourceTypeID: ResourceType.PdfDocument,
      // Preserve the customization capability so the cart's Edit button + the
      // dialog sections show up after a refresh.
      HasQRCode: !!qr,
      HasCoverPage: !!cover,
      HasLocation: !!locationPdf
    } as Resource,
    addQr: !!qr,
    // MachineId (e.g. "3,2,1") holds the QR node ids for this line.
    qrNumbers: qr?.MachineId ?? '',
    qrFileName: qr?.FileName ?? undefined,
    // Preview the generated QR sheet (DynamicPdfs), falling back to the line's
    // own PDF if it's missing.
    qrSheetUrl:
      qr?.DownloadURLWithSas ?? detail.DownloadURLWithSas ?? undefined,
    addCover: !!cover,
    coverTemplateId: cover?.CoverPageName ?? null,
    coverFileName: cover?.FileName ?? undefined,
    // Preview the generated cover PDF (DynamicPdfs), falling back to the line.
    coverPreviewUrl:
      cover?.DownloadURLWithSas ?? detail.DownloadURLWithSas ?? undefined,
    coverFields: cover ? toCoverFields(cover) : undefined,
    addLocation: !!locationPdf,
    // MachineId holds the location id for this line.
    locationId: locationPdf?.MachineId ?? undefined,
    locationFileName: locationPdf?.FileName ?? undefined,
    // Preview the generated location PDF, falling back to the line's own PDF.
    locationPreviewUrl:
      locationPdf?.DownloadURLWithSas ?? detail.DownloadURLWithSas ?? undefined
  };
};

// Lines without a real resource id can't be shown, edited or re-synced — skip
// them rather than inventing synthetic ids for them.
const toCartItems = (details?: ShoppingCartDetail[] | null): PrintOrderItem[] =>
  (details ?? [])
    .filter(detail => Number(detail.ResourceId) > 0)
    .map(toPrintOrderItem);

// Concatenate lists, dropping duplicates by resource id (first wins).
const mergeUnique = (...lists: PrintOrderItem[][]): PrintOrderItem[] => {
  const seen = new Set<number>();
  return lists
    .flat()
    .filter(item =>
      seen.has(item.resource.ID) ? false : seen.add(item.resource.ID)
    );
};

/**
 * On the Resources page, preselect the shopping cart's resources.
 *
 * - No `cartId`: restore the user's active cart (read-only preselect).
 * - Reorder (`cartId` in the URL): fetch both the active cart AND the reordered
 *   order, preselect the union, and persist the merged selection to the server
 *   (so it's saved as the user's cart).
 *
 * Gated behind `usePrintOrderEnabled` (the `shoppingCart` LD flag, and never
 * inside Outlook dialogs). Runs once; the plain restore never
 * clobbers a cart already started this session.
 */
export const useRestoreActiveCart = (): void => {
  const [cartId] = useQueryParam('cartId', StringParam);
  const hydrateActiveCart = usePrintOrderStore(
    state => state.hydrateActiveCart
  );
  const { saveItems } = usePrintOrderCart();
  const restoredRef = useRef(false);

  const enabled = usePrintOrderEnabled();

  // These queries fire on mount. The hook is mounted at the Resources route
  // root, which mounts fresh on a real page entry (home → resources, or reorder
  // from order details) but stays mounted across category / subcategory
  // navigation — so the calls run on page entry, not on every category open.
  //
  // `cacheTime: 0` makes them always fetch fresh (never serve a cached cart).
  // The zustand store is the single source of truth during the session; these
  // only seed it. Not caching the seed means it can never go stale (e.g. after
  // checkout the cart is gone, so a fresh `/active` returns nothing) — no manual
  // cache invalidation to keep in sync.
  //
  // The seed is taken only from a response to THIS mount's request
  // (`isFetchedAfterMount`). Moving Home → Resources → Home remounts the hook
  // before the previous page's entry is collected, so `isSuccess` is true at
  // once with the old cart; seeding from it would resurrect an item removed on
  // the other page until the fresh response arrived.
  const activeQuery = useQuery({
    ...getActiveShoppingCartQuery(),
    enabled,
    cacheTime: 0
  });
  const byIdQuery = useQuery({
    ...getShoppingCartByIdQuery(cartId ?? ''),
    enabled: enabled && !!cartId,
    cacheTime: 0
  });

  useEffect(() => {
    if (restoredRef.current || !enabled) return;

    // Wait for the queries we need to finish (data may be null = no cart).
    if (!activeQuery.isSuccess || !activeQuery.isFetchedAfterMount) return;
    if (cartId && !(byIdQuery.isSuccess && byIdQuery.isFetchedAfterMount))
      return;

    const activeCart = activeQuery.data;
    const activeItems = toCartItems(activeCart?.Details);

    // Plain active restore: preselect the active cart, but don't overwrite a
    // cart already started this session and don't re-save it.
    if (!cartId) {
      restoredRef.current = true;
      if (usePrintOrderStore.getState().items.length > 0) return;
      if (activeItems.length && activeCart) {
        hydrateActiveCart(activeItems, activeCart);
      }
      return;
    }

    // Reorder: preselect the union of the active cart + the reordered order in
    // one shot (so the count doesn't jump), then persist the valid resources.
    restoredRef.current = true;
    const reorderItems = toCartItems(byIdQuery.data?.Details);
    const merged = mergeUnique(activeItems, reorderItems);

    hydrateActiveCart(merged, activeCart ?? null);

    // Update the active order with the full merged array (both APIs' resources).
    if (merged.length) {
      saveItems(merged).catch(() => {
        // error toast handled globally by the mutation cache
      });
    }
  }, [
    enabled,
    cartId,
    activeQuery.isSuccess,
    activeQuery.isFetchedAfterMount,
    activeQuery.data,
    byIdQuery.isSuccess,
    byIdQuery.isFetchedAfterMount,
    byIdQuery.data,
    hydrateActiveCart,
    saveItems
  ]);
};
